/**
 * MentionsPreview.tsx
 *
 * The short list of mentioned conversations under the Communication Center
 * group's Mentions row: up to five unopened conversations, newest first, as
 * "patient · facility", plus a "View all" row when there are more.
 *
 * Mounted by NavGroupItem's expanded branch only, so it fetches only while the
 * group is open in the expanded sidebar, and only while the badge says there is
 * something to list. Its query sits under the ['navigation', 'badges'] prefix so
 * every place that refreshes the nav counts refreshes this list too.
 *
 * A failed request renders nothing: the Mentions count still shows, and the
 * nav is not the place for an error message.
 */
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SidebarMenuSubButton, SidebarMenuSubItem } from '@/components/ui/sidebar';
import { useCloseMobileNav } from '@/hooks/useCloseMobileNav';
import { getMentionPreview, type MentionPreviewRow } from '@/services/communications.service';
import { MENTIONS_NAV_HREF } from '@/components/layout/navBadges';

/** How many conversations the drop-down lists before "View all". */
export const MENTIONS_PREVIEW_LIMIT = 5;

/** Opens this conversation in the Mentions view. */
export function mentionConversationHref(row: Pick<MentionPreviewRow, 'patientId' | 'commLogId'>): string {
  const params = new URLSearchParams({
    view: 'mentions',
    patientId: String(row.patientId),
    commLogId: String(row.commLogId),
  });
  return `/messages?${params.toString()}`;
}

function rowLabel(row: MentionPreviewRow): string {
  const name = row.patientName.trim() || 'Unknown patient';
  return row.facilityName ? `${name} · ${row.facilityName}` : name;
}

const ROW_CLASS = 'pl-4 text-sidebar-foreground/70';

export function MentionsPreview({
  count,
  facilityIds,
  facilityKey,
  accountId,
}: {
  /** Unread mentioned conversations, from the nav badges. */
  count?: number;
  facilityIds: readonly number[];
  /** Stable key for `facilityIds` (see facilityScopeKey). */
  facilityKey: string;
  accountId: number | null;
}) {
  const closeMobileNav = useCloseMobileNav();
  const enabled = !!accountId && (count ?? 0) > 0;

  const { data, isError } = useQuery({
    queryKey: ['navigation', 'badges', 'mentions-preview', accountId, facilityKey],
    queryFn: () => getMentionPreview({ facilityIds, pageSize: MENTIONS_PREVIEW_LIMIT }),
    enabled,
    retry: false,
    staleTime: 60_000,
  });

  if (!enabled || isError || !data) return null;

  const rows = data.data.slice(0, MENTIONS_PREVIEW_LIMIT);
  if (rows.length === 0) return null;

  return (
    <>
      {rows.map((row) => {
        const label = rowLabel(row);
        return (
          <SidebarMenuSubItem key={row.patientId}>
            <SidebarMenuSubButton asChild size="sm" className={ROW_CLASS}>
              <NavLink to={mentionConversationHref(row)} onClick={closeMobileNav} title={label}>
                <span className="truncate">{label}</span>
              </NavLink>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        );
      })}
      {(count ?? 0) > MENTIONS_PREVIEW_LIMIT && (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild size="sm" className={ROW_CLASS}>
            <NavLink to={MENTIONS_NAV_HREF} onClick={closeMobileNav}>
              <span className="truncate">View all</span>
            </NavLink>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      )}
    </>
  );
}
